/**
 * The invoice editor's state, as pure functions.
 *
 * WHY THIS IS NOT A REACT HOOK
 * ---------------------------
 * There is no React testing library in this project, and the alternative to
 * testing logic is not testing it. So every rule about what a valid invoice is —
 * what the fields hold, which lines are usable, what stops the Save button —
 * lives here as plain functions over plain data, and the screen is a thin
 * `useState` shell that calls them.
 *
 * WHAT THE EDITOR DELIBERATELY DOES NOT HAVE
 * ------------------------------------------
 * No tax rate field. The rate comes from the product row in the catalogue, and a
 * rate typed into a form is a second source of truth that can disagree with the
 * catalogue the engine reads. No discount, no charges, no TCS: the engine already
 * supports all three, and each is separate work rather than a field that
 * silently does nothing today.
 */
import { asMoney, parseMoney, parseQuantity, rupeesText, formatQuantity } from '@core/money';
import { isValidIsoDate } from '@domain/financialYear';
import type { NormalizedDocument } from '@core/doc/types';
import type { ProductListItem } from '@data/services/productReader';

/**
 * One editable line, holding the raw TEXT the user typed.
 *
 * Text, not numbers. An `<input>` bound to a number cannot represent "3." while
 * the user is still typing it, and coercing on every keystroke is how a rate
 * silently becomes 0. The text is parsed on read, by the functions below.
 */
export interface DraftLine {
  readonly id: string;
  readonly productId: string;
  readonly quantity: string;
  /** Rupees, as typed ("350", "350.50"). Parsed to paise on read. */
  readonly rate: string;
  /**
   * A description the user typed INSTEAD of the product's own name.
   *
   * Empty means "print the product name", which is what the service falls back
   * to. Keeping it empty rather than pre-filled is what makes the distinction
   * honest: the field shows the product name as a PLACEHOLDER, so an empty box
   * means "I did not change this" and not "I typed the product name".
   *
   * This exists because a shopkeeper legitimately sells something the catalogue
   * cannot describe in one line — a made-to-measure item, a bundle, a job with
   * scope written into it. Without the field the description is whatever the
   * item master happens to say, forever.
   */
  readonly description: string;
  /** Unit of measure for this line (e.g., NOS, SQFT, KG). Defaults to product's unit. */
  readonly unit: string;
  /** GST rate in basis points (e.g., 1800 = 18%). 0 means exempt. Defaults to product's tax rate. */
  readonly taxRateBp: number;
  /** Line discount type. 'NONE' = no discount. */
  readonly discountType: 'NONE' | 'PERCENT' | 'AMOUNT';
  /** PERCENT: milli-percent (12.5% = 12500). AMOUNT: paise. Ignored when discountType is NONE. */
  readonly discountValue: number;
}

export interface InvoiceFormState {
  readonly customerId: string;
  /** 'YYYY-MM-DD'. Empty until picked; the screen defaults it to today. */
  readonly docDate: string;
  readonly lines: readonly DraftLine[];
  readonly roundOff: boolean;
  /**
   * An EXPLICIT place of supply chosen by the user, overriding the resolver.
   *
   * Empty means "use whatever the transaction resolves". It is deliberately NOT a
   * blocker on SAVING: a draft may be saved with the place of supply still
   * unknown, and only ISSUING is gated.
   *
   * Without this field a user whose customer has no usable state would be refused
   * at issue with no way to supply the missing one. The locked decision says to
   * ASK them, and this is where they answer.
   */
  readonly placeOfSupplyOverride: string;
}

/** The payload `issueSalesInvoice` takes, minus the business the caller resolves. */
export interface InvoiceIssueInput {
  readonly customerId: string;
  readonly docDate: string;
  readonly lines: readonly {
    readonly productId: string;
    readonly quantityMilli: number;
    readonly ratePaise: number;
    /** Override GST rate in basis points (e.g., 1800 = 18%). Falls back to product's tax rate if not provided. */
    readonly taxRateBp?: number;
    /**
     * The user's own wording, when they gave some.
     *
     * OPTIONAL AND ABSENT WHEN BLANK — never an empty string. The service falls
     * back with `line.description ?? product.name`, and `??` only falls back on
     * `null`/`undefined`. Sending `''` would therefore store an EMPTY description,
     * which the schema rejects (`min(1)`) and which `renderA4` would print as a
     * blank item. Blank must arrive as an absent key, not as `''`.
     */
    readonly description?: string;
    /** Unit of measure for this line (e.g., NOS, SQFT, KG). */
    readonly unit?: string;
  }[];
  readonly roundOff: boolean;
  /** The user's explicit place of supply. Empty = resolve from the transaction. */
  readonly placeOfSupplyOverride?: string;
}

/** A fresh form: one empty line, no customer, no round-off. */
export function emptyInvoiceForm(lineId: string): InvoiceFormState {
  return {
    customerId: '',
    docDate: '',
    roundOff: false,
    lines: [{ id: lineId, productId: '', quantity: '1', rate: '', description: '', unit: 'NOS', taxRateBp: 0, discountType: 'NONE', discountValue: 0 }],
    placeOfSupplyOverride: '',
  };
}

/** A blank line added to the bottom of the editor. */
export function blankLine(id: string): DraftLine {
  return { id, productId: '', quantity: '1', rate: '', description: '', unit: 'NOS', taxRateBp: 0, discountType: 'NONE', discountValue: 0 };
}

/**
 * Quantity in milli-units, or 0 when the text is not a number at all.
 *
 * `parseQuantity` THROWS on "abc" and on excess precision, which is right for a
 * service and lethal in a render path: this runs on every keystroke, so one
 * stray character would blank the screen. A half-typed field is a normal state,
 * not an exception, so a bad value reads as 0 and `lineProblem` is what tells
 * the user about it.
 */
export function safeQuantity(text: string, precision = 3): number {
  try {
    return parseQuantity(text, precision);
  } catch {
    return 0;
  }
}

/** Rate in paise, or 0 when the text is not a number. See `safeQuantity`. */
export function safeRate(text: string): number {
  try {
    return parseMoney(text === '' ? '0' : text);
  } catch {
    return 0;
  }
}

/** Format milli-units to display string (e.g., 1000 → "1", 1500 → "1.5"). */
export const formatQuantityDisplay = formatQuantity;

/**
 * Why one line cannot be saved yet, or null when it can.
 *
 * A line is unusable with no product (nothing to price) or a quantity that is not
 * positive (a line of zero is not a line). The rate is deliberately NOT required
 * to be non-zero: giving something away is a real thing shops do, and a form
 * that refuses it is a form people work around.
 */
export function lineProblem(
  line: DraftLine,
  productsById: ReadonlyMap<string, ProductListItem>,
): string | null {
  if (line.productId === '') return 'Choose an item.';
  if (!productsById.has(line.productId)) return 'That item is no longer in the catalogue.';
  if (safeQuantity(line.quantity) <= 0) return 'Quantity must be more than zero.';
  return null;
}

/** True when every line is usable. Drives the Save button. */
export function linesAreUsable(
  lines: readonly DraftLine[],
  productsById: ReadonlyMap<string, ProductListItem>,
): boolean {
  return lines.length > 0 && lines.every((l) => lineProblem(l, productsById) === null);
}

/**
 * Why the invoice cannot be saved yet, or null when it can.
 *
 * Ordered the way the user meets it — customer, date, then items — so the message
 * names the first thing missing rather than whichever check happened to run first.
 */
export function formProblem(
  state: InvoiceFormState,
  productsById: ReadonlyMap<string, ProductListItem>,
): string | null {
  if (state.customerId === '') return 'Choose a customer.';
  // Checked before anything calls financialYearFor, which THROWS on a bad date.
  if (!isValidIsoDate(state.docDate)) return 'Choose a date.';
  if (state.lines.length === 0) return 'Add at least one item.';
  for (const line of state.lines) {
    const problem = lineProblem(line, productsById);
    if (problem !== null) return problem;
  }
  return null;
}

/** What to send the service, or null when the form is not ready. */
export function toIssueInput(
  state: InvoiceFormState,
  productsById: ReadonlyMap<string, ProductListItem>,
): InvoiceIssueInput | null {
  if (formProblem(state, productsById) !== null) return null;
  return {
    customerId: state.customerId,
    docDate: state.docDate,
    lines: state.lines.map((l) => ({
      productId: l.productId,
      quantityMilli: safeQuantity(l.quantity),
      ratePaise: safeRate(l.rate),
      // Include taxRateBp (0 = 0%, 1800 = 18%, etc.). Always send it so the
      // invoice line's GST override is persisted. The service will use this
      // instead of the product's tax rate when provided.
      taxRateBp: l.taxRateBp,
      // Spread, not `description: l.description`. An empty string would survive
      // `??` in the service and get STORED as the line's description; see the note
      // on `InvoiceIssueInput`. A blank box must send no key at all.
      ...(l.description.trim() ? { description: l.description.trim() } : {}),
      // Include unit as a snapshot - service will use it if provided
      ...(l.unit ? { unit: l.unit } : {}),
    })),
    roundOff: state.roundOff,
    placeOfSupplyOverride: state.placeOfSupplyOverride || undefined,
  };
}

/**
 * The editor's lines as the calculation engine needs them, in paise and
 * milli-units.
 *
 * Separate from `toIssueInput` because the live preview needs these values on
 * every keystroke, long before the form is complete enough to submit. Sharing
 * one converter is the point: if the preview parsed quantities one way and the
 * submit path another, the total on screen would belong to a different invoice
 * from the one saved.
 */
export function toDraftLines(lines: readonly DraftLine[]): {
  readonly productId: string;
  readonly quantityMilli: number;
  readonly ratePaise: number;
  readonly taxRateBp: number;
}[] {
  return lines.map((l) => ({
    productId: l.productId,
    quantityMilli: safeQuantity(l.quantity),
    ratePaise: safeRate(l.rate),
    taxRateBp: l.taxRateBp ?? 0,
  }));
}

/**
 * Load an existing DRAFT invoice into the editor's form state.
 *
 * WHY THIS IS A PURE FUNCTION AND NOT SCREEN CODE
 * -----------------------------------------------
 * Reopening a draft and reconstructing its form is a rule with a wrong answer
 * waiting in it — the same reasoning as the rest of this file. Getting it wrong
 * does not throw; it silently shows quantities in the wrong unit, or a rate that
 * has been through an Indian-grouped formatter and can no longer be retyped, and
 * the user saves their way to a different invoice than the one they opened.
 *
 * The two conversions that are easy to get wrong, and why:
 *
 *   - `quantityMilli` is MILLI-units and `rupeesText` is RUPEES, so the quantity
 *     is divided by 1000 and the rate is not. Copying one formula for both is
 *     how a draft reopens showing "2000 NOS" of something the user sold 2 of.
 *   - The rate goes through `rupeesText`, NOT `formatAmount`. `formatAmount`
 *     produces "1,000.00" with a thousands separator, and that string in an
 *     `<input>` has to have its commas deleted before the user can type
 *     anything. `rupeesText` is the inverse of `parseMoney` and exists for
 *     exactly this.
 *
 * The two conversions that are easy to get wrong, and why:
 *
 *   - `quantityMilli` is MILLI-units and `rupeesText` is RUPEES, so the quantity
 *     is divided by 1000 and the rate is not. Copying one formula for both is
 *     how a draft reopens showing "2000 NOS" of something the user sold 2 of.
 *   - The rate goes through `rupeesText`, NOT `formatAmount`. `formatAmount`
 *     produces "1,000.00" with a thousands separator, and that string in an
 *     input box has to have its commas deleted before the user can type
 *     anything. `rupeesText` is the inverse of `parseMoney` and exists for
 *     exactly this.
 */
export function formFromDraft(
  draft: NormalizedDocument,
  lineId: (n: number) => string,
  /**
   * The catalogue, used ONLY to tell a user-typed description from the fallback.
   *
   * Optional so that reopening a draft whose products are all gone still works:
   * an unknown product simply cannot be compared, and its stored description is
   * shown as-is rather than being discarded.
   */
  productsById?: ReadonlyMap<string, ProductListItem>,
): InvoiceFormState {
  return {
    customerId: draft.party.id,
    docDate: draft.docDate,
    // Round-off is a stored RESULT (an amount), not a stored preference (a
    // boolean), so "is round-off on" has to be inferred. The inference is
    // exact: the two agree exactly when nothing was rounded.
    roundOff: draft.totals.preRoundOffPaise !== draft.totals.grandTotalPaise,
    // The RESOLVED place of supply, not the customer's state. A draft saved
    // with an explicit override must reopen showing that override, or editing
    // it silently discards the answer the user gave.
    placeOfSupplyOverride: draft.placeOfSupplyCode,
lines: draft.lines.map((l, i) => ({
      id: lineId(i),
      // A line whose product has since been deleted arrives with no id. It is
      // kept as an unselected line rather than dropped: silently removing a
      // line the user sold would be the worst possible edit.
      productId: l.productId ?? '',
      quantity: formatQuantity(l.quantityMilli),
      rate: rupeesText(asMoney(l.ratePaise)),
      // The stored description is ALWAYS the product's name when the user did
      // not type one -- the service falls back with `?? product.name`. Loading
      // that into the box would show every line as if it had been written by
      // hand, and saving would then freeze a copy of the product name into the
      // invoice as a frozen description. Comparing against the catalogue is what
      // keeps "I typed this" and "this is the product" apart.
      //
      // A product that has since been RENAMED therefore shows its old frozen
      // name in the box, which is correct: that is what the invoice says.
      description:
        l.productId !== undefined &&
        productsById?.get(l.productId)?.product.name === l.description
          ? ''
          : l.description,
      // Unit is stored on the line as a snapshot; fall back to product's unit or NOS
      unit: ((): string => {
        let productUnit: string = 'NOS';
        if (productsById && l.productId) {
          const item = productsById.get(l.productId);
          if (item && item.product.unit) {
            productUnit = item.product.unit;
          }
        }
        return l.unit ?? productUnit ?? 'NOS';
      })(),
      // Tax rate is stored on the line as a snapshot (historical immutability).
      // Load the stored taxRateBp directly; do NOT reload from Product Master.
      taxRateBp: l.taxRateBp ?? 0,
      // Discount fields are not persisted in NormalizedLine (only discountPaise is).
      // Default to NONE/0 when reopening a draft — the user can re-enter if needed.
      discountType: 'NONE' as const,
      discountValue: 0,
    })),
  };
}

/**
 * When an item is picked, its catalogue rate becomes the line's starting rate.
 *
 * A STARTING rate, not a fixed one — the user overwrites it freely. It exists to
 * save typing, and it is read from the catalogue rather than remembered from the
 * last invoice so it cannot go stale.
 */
export function lineForProduct(line: DraftLine, product: ProductListItem): DraftLine {
  return {
    ...line,
    productId: product.product.id,
    // Paise -> editable rupees text, through core. Not `toFixed` (banned in
    // features/), and not `formatAmount` either: grouping separators inside an
    // input box would have to be deleted before a rate could be typed.
    rate: rupeesText(asMoney(product.product.salePricePaise)),
    // Use the product's default unit
    unit: product.product.unit || 'NOS',
    // Default GST from the product's tax rate
    taxRateBp: product.taxRateBp ?? 0,
    // A description typed against the PREVIOUS item cannot follow the new one:
    // the line keeps the same quantity, so swapping the product used to leave the
    // old wording sitting under a different item -- an invoice that sold one
    // thing and described another. The rate is deliberately NOT blanked on a
    // re-pick (see `applyProductPick`), but the wording is always about the item
    // that was just replaced, so it always goes.
    description: '',
  };
}

/**
 * Choose an item on ONE line, leaving every other line exactly as it was.
 *
 * This is the rule, extracted from the screen because the screen got it wrong in
 * a way no test could see. The handler used to be a single ternary over all
 * lines:
 *
 *     l.id === id && product ? lineForProduct(l, product) : { ...l, productId }
 *
 * The FALSE branch is every line that is NOT the one being edited, and it wrote
 * the new product onto all of them. So picking an item on line 2 silently
 * overwrote line 1, and an invoice with four different items came out with the
 * same item on all four — each still holding its own quantity and rate, so the
 * result looked like a deliberate order rather than a bug.
 *
 * It lives here, as a pure function, for the reason the rest of this file does:
 * a rule inside a React closure is a rule nothing checks.
 *
 * Clearing the choice (`productId === ''`) must not blank the rate, because a
 * user who reconsiders a selection has not decided to retype the price.
 */
export function applyProductPick(
  lines: readonly DraftLine[],
  lineId: string,
  product: ProductListItem | undefined,
  productId: string,
): DraftLine[] {
  return lines.map((l) => {
    if (l.id !== lineId) return l;
    if (product) return lineForProduct(l, product);
    return { ...l, productId };
  });
}